import { sanitizeCenterTextInput } from "./centerTextRules";
import { CanvasTexture } from "three";
import { LinearFilter } from "three";

const MIN_FONT = 12;
const MAX_FONT = 560;
const MAX_CANVAS_AXIS = 4096;
const PAD = 20;
/** Line spacing multiplier vs font size (multi-line center text). */
const LINE_HEIGHT_RATIO = 1.28;
const GENERIC_FONT_FAMILIES = new Set([
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-serif",
  "ui-sans-serif",
  "ui-monospace",
  "ui-rounded",
  "emoji",
  "math",
  "fangsong",
]);

/**
 * One shared 1x1 probe canvas for `measureText` calls. Each `makeTextTexture`
 * invocation previously allocated two fresh `<canvas>` elements just to
 * measure font metrics — for wall text we call this 2× per channel × on every
 * text edit, and the GC pressure showed up in long-frame counts. Reusing one
 * 2D context is safe because we never read its pixels back, only `.font` /
 * `.measureText`. Lazy because `document` isn't available at module load in
 * SSR sanity checks.
 */
let _probeCtx: CanvasRenderingContext2D | null | undefined;
function getProbeCtx(): CanvasRenderingContext2D | null {
  if (_probeCtx !== undefined) return _probeCtx;
  if (typeof document === "undefined") {
    _probeCtx = null;
    return null;
  }
  _probeCtx = document.createElement("canvas").getContext("2d");
  return _probeCtx;
}

function normalizeFontFamily(fontFamily: string): string {
  const parts = fontFamily
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return "sans-serif";
  return parts
    .map((part) => {
      if (part.startsWith('"') || part.startsWith("'")) return part;
      if (GENERIC_FONT_FAMILIES.has(part.toLowerCase())) return part;
      return `"${part.replace(/"/g, '\\"')}"`;
    })
    .join(", ");
}

export type MakeTextTextureOptions = {
  /** Font size in canvas pixels. */
  fontSizePx: number;
  /**
   * Max width (px) for one line of text before word-wrap (mirror aperture width
   * in canvas space, passed from `MirrorScene`).
   */
  maxLineWidthPx: number;
  /**
   * `pw / ph` of the text plane in world space on the front layer (`MirrorScene`).
   * When provided, the canvas keeps the same aspect ratio so text edits do not
   * cause 3D scale "jumping".
   */
  texturePlaneAspect?: number;
  /** Mirror rendered glyphs horizontally (used for mirrored depth layers). */
  flipX?: boolean;
  /** Mirror rendered glyphs vertically (used for mirrored depth layers). */
  flipY?: boolean;
  /** Force single-line rendering without word-wrap (used by wall text). */
  singleLine?: boolean;
  /**
   * Optional outline ("contrast") drawn around each glyph before the fill
   * pass — `colorHex` is the stroke colour (white for "light" contrast,
   * black for "dark"); `widthPx` is the total stroke width in canvas pixels
   * (canvas2d strokes are centred on the path, so visible thickness on each
   * side is `widthPx / 2`). Omit `outline` for no stroke.
   */
  outline?: { colorHex: string; widthPx: number };
};

function splitParagraphs(text: string): string[] {
  return text.split(/\r\n|\r|\n/);
}

function breakLongToken(
  token: string,
  maxW: number,
  measure: (s: string) => number,
): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < token.length) {
    if (measure(token[i]!) > maxW) {
      out.push(token[i]!);
      i += 1;
      continue;
    }
    let lo = i + 1;
    let hi = token.length;
    let best = i + 1;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      const sub = token.slice(i, mid);
      if (measure(sub) <= maxW) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    if (best <= i) {
      out.push(token[i]!);
      i += 1;
    } else {
      out.push(token.slice(i, best));
      i = best;
    }
  }
  return out;
}

/** Word-wrap one paragraph to lines ≤ `maxW` (px). Preserves spaces where possible. */
function wrapParagraphToWidth(
  paragraph: string,
  maxW: number,
  measure: (s: string) => number,
): string[] {
  const trimmed = paragraph.trimEnd();
  if (trimmed.length === 0) return [" "];

  const words = trimmed.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";

  const flush = () => {
    if (current.trim().length > 0) {
      lines.push(current.trimEnd());
      current = "";
    }
  };

  for (const word of words) {
    const trial = current.length > 0 ? `${current} ${word}` : word;
    if (measure(trial) <= maxW) {
      current = trial;
      continue;
    }
    flush();
    if (measure(word) <= maxW) {
      current = word;
    } else {
      lines.push(...breakLongToken(word, maxW, measure));
      current = "";
    }
  }
  flush();
  return lines.length > 0 ? lines : [" "];
}

function buildAllLines(
  safe: string,
  maxLineWidth: number,
  fontSize: number,
  fontFamily: string,
  bold: string,
): string[] {
  const paragraphs = splitParagraphs(safe);
  const probe = getProbeCtx();
  if (!probe) return [" "];

  const familyCss = normalizeFontFamily(fontFamily);
  const measure = (s: string) => {
    probe.font = `${bold}${fontSize}px ${familyCss}`;
    return probe.measureText(s).width;
  };

  const innerW = Math.max(32, maxLineWidth);
  const out: string[] = [];
  for (const para of paragraphs) {
    if (para.length === 0) {
      out.push(" ");
      continue;
    }
    out.push(...wrapParagraphToWidth(para, innerW, measure));
  }
  return out.length > 0 ? out : [" "];
}

export function makeTextTexture(
  text: string,
  colorHex: string,
  fontFamily: string,
  options: MakeTextTextureOptions,
): CanvasTexture | null {
  const familyCss = normalizeFontFamily(fontFamily);
  const safe = sanitizeCenterTextInput(text);
  if (!safe) return null;

  const fontSize = Math.min(MAX_FONT, Math.max(MIN_FONT, Math.round(options.fontSizePx)));
  const maxLineWidthPx = Math.max(
    32,
    Math.min(MAX_CANVAS_AXIS - PAD * 2, Math.round(options.maxLineWidthPx)),
  );

  const bold = "bold ";
  const displayLines = options.singleLine
    ? [safe.replace(/\r\n|\r|\n/g, " ").replace(/\s+/g, " ").trim() || " "]
    : buildAllLines(safe, maxLineWidthPx, fontSize, fontFamily, bold);

  const lh = fontSize * LINE_HEIGHT_RATIO;
  const blockH = displayLines.length * lh;

  /**
   * Canvas width must track the **widest rendered line**, not the full wrap column width.
   * Otherwise iw/ih is huge (wide empty canvas + one short line) and `logoPlaneSize` in
   * `MirrorScene` collapses the plane height in world units — text looks tiny (e.g. MAYTRIX @ 45px).
   */
  const measureCtx = getProbeCtx();
  if (!measureCtx) return null;
  measureCtx.font = `${bold}${fontSize}px ${familyCss}`;
  let maxLineTextW = 0;
  for (const line of displayLines) {
    maxLineTextW = Math.max(maxLineTextW, measureCtx.measureText(line).width);
  }
  maxLineTextW = Math.max(maxLineTextW, fontSize * 0.35);
  // The stroke is drawn centred on each glyph path, so it pokes out of the
  // measured text box by `outline.widthPx` on every side (we draw a stroke
  // of total width `widthPx * 2`, half of which sits outside the path).
  // Reserve that overshoot in the canvas size + bump padding so the outline
  // never gets clipped by the edge of the texture.
  const outlineOvershoot = options.outline ? Math.ceil(options.outline.widthPx) : 0;
  const padded = PAD + outlineOvershoot;
  maxLineTextW += outlineOvershoot * 2;
  const contentW = Math.min(maxLineWidthPx, Math.ceil(maxLineTextW));
  const minCw = Math.max(48, Math.ceil(contentW + padded * 2));
  const minCh = Math.max(48, Math.ceil(blockH + padded * 2));

  const ar = options.texturePlaneAspect;
  const flipX = options.flipX === true;
  const flipY = options.flipY === true;
  let cw: number;
  let ch: number;
  if (ar && ar > 0 && Number.isFinite(ar)) {
    /**
     * Fixed texel ↔ world mapping: canvas size must **not** track the tight
     * text bbox width (that grew with each character and, with `ch ≈ cw/ar`,
     * inflated `ch` on wide mirrors → glyphs shrank while typing).
     *
     * Use the full wrap column width + padding; only grow when content needs
     * more height (extra lines), then bump `cw` with `ch` to keep `cw/ch ≈ ar`.
     */
    const cwColumn = Math.min(
      MAX_CANVAS_AXIS,
      Math.max(48, Math.ceil(maxLineWidthPx + PAD * 2)),
    );
    /** Satisfy `cw ≥ cwColumn`, `ch ≥ minCh`, and `cw/ch ≈ ar` (integer pixels). */
    let cw0 = cwColumn;
    let ch0 = Math.max(minCh, Math.ceil(cw0 / ar));
    for (let i = 0; i < 6; i++) {
      cw0 = Math.min(MAX_CANVAS_AXIS, Math.max(cwColumn, Math.ceil(ch0 * ar)));
      ch0 = Math.min(MAX_CANVAS_AXIS, Math.max(minCh, Math.ceil(cw0 / ar)));
    }
    cw = cw0;
    ch = ch0;
  } else {
    cw = Math.min(MAX_CANVAS_AXIS, minCw);
    ch = Math.min(MAX_CANVAS_AXIS, minCh);
  }

  const c2d = document.createElement("canvas");
  c2d.width = cw;
  c2d.height = ch;
  const ctx = c2d.getContext("2d");
  if (!ctx) return null;

  ctx.clearRect(0, 0, cw, ch);
  ctx.font = `${bold}${fontSize}px ${familyCss}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = colorHex;

  if (flipX) {
    ctx.translate(cw, 0);
    ctx.scale(-1, 1);
  }
  if (flipY) {
    ctx.translate(0, ch);
    ctx.scale(1, -1);
  }

  const outline = options.outline;
  if (outline && outline.widthPx > 0) {
    // Stroke is centred on the path; double the width so half of it lives
    // *outside* the glyph silhouette and the fill on top still reads at the
    // user's chosen colour without thinning.
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = outline.colorHex;
    ctx.lineWidth = outline.widthPx * 2;
  }

  const top = (ch - blockH) / 2;
  displayLines.forEach((line, i) => {
    const y = top + (i + 0.5) * lh;
    if (outline && outline.widthPx > 0) {
      ctx.strokeText(line, cw / 2, y);
    }
    ctx.fillText(line, cw / 2, y);
  });

  const tex = new CanvasTexture(c2d);
  tex.colorSpace = "srgb";
  tex.generateMipmaps = false;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.needsUpdate = true;
  /**
   * Tight UV bbox of the rendered text block inside the canvas — used by the
   * RGB-wheel shader to scale the rainbow pattern to the *letters*, not to
   * the full padded canvas. Bounds are symmetric around (0.5, 0.5) because
   * `textAlign = center` + `top = (ch - blockH) / 2` centers the block on
   * both axes; flipY of CanvasTexture mirrors only the V axis, which is also
   * symmetric so it doesn't change min/max.
   */
  const halfU = Math.min(0.5, maxLineTextW / (2 * cw));
  const halfV = Math.min(0.5, blockH / (2 * ch));
  tex.userData.uvBounds = [
    0.5 - halfU,
    0.5 - halfV,
    0.5 + halfU,
    0.5 + halfV,
  ] as [number, number, number, number];
  return tex;
}
